import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { ME_QUERY_KEY } from "../features/shell/api/me.ts";
import { ApiClientProvider } from "../shared/api/api-provider.tsx";
import { ApiRequestError, createApiClient, type ApiClient } from "../shared/api/client.ts";
import { I18nProvider, type I18nProviderProps } from "../shared/i18n/i18n-provider.tsx";
import { ThemeProvider } from "../shared/theme/theme-provider.tsx";
import { Toasts } from "../shared/toasts.tsx";

/** Client errors (4xx) are final; only network and server failures are retried, at most twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: shouldRetry, refetchOnWindowFocus: false, staleTime: 30_000 },
    },
  });
}

/**
 * A call answered `unauthenticated` ends the session view: `/me` is fetched again, sees the same
 * answer and the shell sends the user to login. A `/me` fetch already in flight is not cancelled
 * (`cancelRefetch: false`): when `/me` itself is the call answered `unauthenticated`, cancelling it
 * would start a new `/me` that ends the same way, in a loop that never reaches the login screen.
 */
export function endSessionView(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }, { cancelRefetch: false });
}

export interface ProvidersProps extends Omit<I18nProviderProps, "children"> {
  readonly children: ReactNode;
  readonly queryClient?: QueryClient;
  readonly apiClient?: ApiClient;
}

/** Query cache, API client, theme, i18n and toasts; session handling lives in the client and the shell. */
export function Providers({ children, queryClient, apiClient, ...i18n }: ProvidersProps) {
  const [fallbackClient] = useState(createQueryClient);
  const client = queryClient ?? fallbackClient;
  const [fallbackApi] = useState(() =>
    createApiClient({
      onUnauthorized: () => {
        endSessionView(client);
      },
      // A tab opened before the deploy still sends `environment` (design D6): load the new bundle.
      onStaleBundle: () => {
        window.location.reload();
      },
    }),
  );
  return (
    <QueryClientProvider client={client}>
      <ApiClientProvider client={apiClient ?? fallbackApi}>
        <ThemeProvider>
          <I18nProvider {...i18n}>
            <Toasts>{children}</Toasts>
          </I18nProvider>
        </ThemeProvider>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
